import React, { Component } from 'react';
import { get, set, sumBy, orderBy, random, sortBy } from 'lodash';
import styled from 'styled-components';
import { connect } from 'react-redux';
import memoize from 'fast-memoize';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchCampaign, fetchCampaignCallouts } from 'services/campaign/duck';
import { fetchLayerTwoSummary } from 'services/layerTwo/duck';
import { fetchChannelCallouts } from 'services/channel/duck';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import helpers from 'helpers';

// Components and Containers
import { Grid, Row, Col } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import StyledSelect from 'components/atoms/inputs/Select';
import RenderingTile from 'components/molecules/RenderingTile';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import GeoBreakdownMap from 'components/molecules/charts/labs/GeoBreakdownMap';
import GeoDonutChart from 'components/molecules/charts/labs/GeoDonutChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import LabsCountrybyProductPivotTable from 'components/molecules/tables/labs/LabsCountrybyProductPivotTable';
import LineChart from 'components/molecules/charts/LineChart';
import RealValueAudiences from 'components/molecules/charts/labs/RealValueAudiences';

// Constants
import { MAGIC_TOP_LEVEL_GEO } from 'constants/labs';

/*
******************************************************************************
NOTE: THIS IS POC DEMO CODE. NOT REFLECTIVE OF PATTERNS THAT WE WANT TO
      USE THROUGHOUT THE APP. IF WE WANT TO MOVE THIS FEATURE TO TRUE
      CLIENT FACING FEATURE, WE WILL NEED TO REFACTOR THE FUNCTIONS BELOW.

WARNING: THIS FILE INCLUDE DATA GENERATORS THAT SHOULD NOT BE USED IN CLIENT
         FACING FEATURES.
******************************************************************************
*/

const countryKeys = {
    Indonesia: 'id',
    Japan: 'jp',
    Malaysia: 'my',
    'South Korea': 'kr',
    Thailand: 'th',
    Vietnam: 'vn',
};

const BrandSelect = styled(StyledSelect)`
    width: 300px;
`;

const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};

const rvDataGenerator = memoize((data) => {
    const allData = {};
    for (const child of data) {
        const audiences = shuffleArray([
            'Value Concious Shoppers', 'Healthy Food Shoppers', 'Blog Readers',
            'Family Fun Lifestyle', 'Pregnancy & Maternity', 'B2B - eCommerce',
            'Sports - Football', 'Music Enthusiasts'
        ]);
        set(allData, [child.entity_name], sortBy([{
            audience: audiences[0],
            costPerSale: `$${(child.ecpa * random(0.15, 1.5)).toFixed(2)}`,
            realValue: random(1.05, 1.6).toFixed(2)
        },
        {
            audience: audiences[1],
            costPerSale: `$${(child.ecpa * random(0.15, 1.5)).toFixed(2)}`,
            realValue: random(1.05, 1.6).toFixed(2)
        },
        {
            audience: audiences[2],
            costPerSale: `$${(child.ecpa * random(0.15, 1.5)).toFixed(2)}`,
            realValue: random(1.05, 1.6).toFixed(2)
        },
        {
            audience: audiences[3],
            costPerSale: `$${(child.ecpa * random(0.15, 1.5)).toFixed(2)}`,
            realValue: random(1.05, 1.6).toFixed(2)
        },
        {
            audience: audiences[4],
            costPerSale: `$${(child.ecpa * random(0.15, 1.5)).toFixed(2)}`,
            realValue: random(1.05, 1.6).toFixed(2)
        }], ['realValue']).reverse());
    }
    return allData;
});

const generateTimeSeriesReach = memoize((data, buckets) => {
    const reachTimeSeriesData = {};
    for (const child of data) {
        set(reachTimeSeriesData, [child.entity_name], []);
        for (const date of buckets) {
            reachTimeSeriesData[child.entity_name].push(
                { date_in_ms: date, reach: parseInt(child.impressions * random(0.052, 0.113), 10) }
            );
        }
    }
    return reachTimeSeriesData;
});


const generateReach = memoize(
    data => data.map(r => ({ ...r, reach: parseInt(r.impressions * random(0.2, 0.35), 10) }))
);

const generateReachByGeo = memoize((data) => { // eslint-disable-line no-unused-vars
    const allKeys = [MAGIC_TOP_LEVEL_GEO, 'Indonesia', 'Japan', 'Malaysia', 'South Korea', 'Thailand', 'Vietnam']; // WARNING: HARD CODED GEOS

    return allKeys.reduce((prev, key) => {
        const mobile = random(0.833, 0.944);
        const other = random(0.005, 0.01);
        const pc = 1 - mobile - other;
        set(prev, [key], {
            PC: parseFloat(pc.toFixed(4)),
            Mobile: parseFloat(mobile.toFixed(4)),
            Other: parseFloat(other.toFixed(4))
        });
        return prev;
    }, {});
});

const generateGeoProductData = memoize((data) => {
    const expandedData = [];
    for (const child of data) {
        let i = 0;
        const p1 = random(0.45, 0.66);
        const p2 = random(0.08, 0.23);
        const p3 = 1 - p1 - p2;
        const multipliers = [p1, p2, p3];

        const s1 = random(0.39, 0.42);
        const s2 = random(0.07, 0.21);
        const s3 = 1 - s1 - s2;
        const spendMultipliers = [s1, s3, s2]; // Intentionally out of order

        for (const product of shuffleArray([ // WARNING: HARD CODED PRODUCTS
            'Toothbrush - CrissCross Ultrathin',
            'Toothbrush - CrossAction ProHealth 7',
            'Toothbrush - Pro Gum Care'
        ])) {
            const reach = Math.floor(child.reach * multipliers[i]);
            const impressions = child.impressions * multipliers[i];
            const activities = Math.floor(child.ecpa_activities * multipliers[i]);
            const spend = child.spend * spendMultipliers[i];
            const ecpaSpend = child.ecpa_spend * spendMultipliers[i];
            expandedData.push({
                ...child,
                product,
                reach,
                spend,
                impressions,
                ecpa: helpers.safeDivide(ecpaSpend, activities),
                ecpa_spend: ecpaSpend,
                ecpa_activities: activities,
                total_activities: activities
            });
            i += 1;
        }
    }
    return expandedData;
});

const generateProductDonutData = memoize(data => Object.keys(countryKeys).reduce((prev, key) => {
    const productData = data.filter(e => e.entity_name === key);
    const total = sumBy(productData, 'reach');
    for (const { product, reach } of productData) {
        set(prev, [key, product], (reach / total) * 100);
    }
    return prev;
}, {}));

class LabGeoDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            entityId: null,
            country: MAGIC_TOP_LEVEL_GEO, // WARNING: HARD CODED MAGIC VALUE
            entityLookup: {},
        };
    }

    static getDerivedStateFromProps(props, state) {
        const entityId = parseInt(props.entityId || props.match.params.entityId, 10);

        const entityLookup = {};
        if (props.layerTwo.entity.summary.hasData) {
            props.layerTwo.entity.summary.data.forEach(e => (entityLookup[e.entity_name] = e.entity_id));
        }

        const newState = { ...state };
        let touch = false;
        if (entityId !== state.entityId) {
            newState.entityId = entityId;
            touch = true;
        }

        if (entityLookup !== state.entityLookup) {
            newState.entityLookup = entityLookup;
            touch = true;
        }

        if (touch) {
            return newState;
        }

        return null;
    }

    componentDidMount() {
        this.makeRequestsForData();
    }

    createPayload(entityId) {
        return {
            transactionId: `entity-${entityId}-lab-geo`,
            entityId
        };
    }

    makeRequestsForData() {
        const entityId = this.state.entityId;
        const payload = this.createPayload(entityId);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchCampaign(payload);
        if (this.state.country === MAGIC_TOP_LEVEL_GEO) { // WARNING: HARD CODED MAGIC VALUE
            this.props.fetchCampaignCallouts(payload);
        } else {
            const aPayload = this.createPayload(this.state.entityLookup[this.state.country]);
            this.props.fetchCampaignCallouts(aPayload);
        }
        this.props.fetchLayerTwoSummary(payload);
    }

    componentDidUpdate(prevProps, prevState) {
        const isEntityChanged = prevState.entityId !== this.state.entityId;
        const isTimeRangeChanged = prevProps.selectedTimeRange !== this.props.selectedTimeRange;
        const isCountryChanged = prevState.country !== this.state.country;
        if (isTimeRangeChanged || isEntityChanged || isCountryChanged) {
            this.makeRequestsForData();
        }
    }

    getCurrentEntityId() {
        return this.state.entityId;
    }

    changeCountry = (country) => {
        let newCountry = country;
        if (country === this.state.country) {
            newCountry = MAGIC_TOP_LEVEL_GEO; // WARNING: HARD CODED MAGIC VALUE
        }
        this.setState({
            ...this.state,
            country: newCountry
        });
    }

    render() {
        const stub = 'l3-summary';
        const {
            backfillBuckets,
            entityObjective,
            entityObjectiveLabelCode,
        } = this.props;
        const { country } = this.state;
        const {
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            ecpaTerm,
            helpKeySuffix,
            showActivitiesData
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const hasSummaryData = get(this.props, 'summary.hasData', null);
        const hasCalloutData = get(this.props, 'summary.callouts.params.hasData', null);
        const hasGeoData = get(this.props, 'layerTwo.entity.summary.hasData', null);
        const callouts = get(this.props, 'summary.callouts.data', {});
        const calloutWindow = get(this.props, 'summary.callouts.params.timeRanges.previous.windowSize', null);

        const topLevelSelected = country === MAGIC_TOP_LEVEL_GEO; // WARNING: HARD CODED

        const calloutSettings = [
            { key: 'impressions', title: 'Impressions', format: '0,0' },
            {
                key: eCPAActivitiesOrClicks,
                helpKey: `${eCPAActivitiesOrClicks}${helpKeySuffix}`,
                title: eCPAActivitiesOrClicksTerm,
                format: '0,0'
            }
        ];

        if (showActivitiesData) {
            calloutSettings.push({
                key: 'ecpa',
                helpKey: `ecpa${helpKeySuffix}`,
                title: entityObjectiveLabelCode ? `Average ${ecpaTerm}` : 'eCPA',
                format: '$0,0.00',
                invert: true
            });
        } else {
            calloutSettings.push({ key: 'ctr', title: 'CTR', format: '0.00%' });
        }

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow
        });

        // If we have data to render, let us render!
        const hasData = [
            hasSummaryData === null ? true : hasSummaryData,
            hasCalloutData
        ].some(d => d);

        const ChannelActions = <React.Fragment>
            <label style={{ margin: '0 5px 0 0' }}>BRANDS:</label>
            <BrandSelect>
                <option selected="selected">Oral B</option>
                <option disabled >Pantene - Pending Data</option>
                <option disabled >Pampers - Pending Data</option>
            </BrandSelect>
        </React.Fragment>;


        // NOTE: The code below should NOT be in a `render` call in a client facing feature.
        const geoData = [];
        const childTotals = {};
        const geoDonutData = {};
        const metrics = ['impressions', 'reach', 'spend', 'ecpa_activities'];
        let orderedGeos = [];
        let orderedProducts = [];
        let childSummaryTableData = [];
        let sortedChildSummaryTableData = [];
        let geoReachTimeSeriesData = {}; // eslint-disable-line no-unused-vars
        let rvData = {};
        let reachByGeo = {};
        let geoProductData = [];
        let productDonutData = {};
        let GeoOrProductDonut = null;
        let geoOrProducttwoAxisChart = null;
        if (hasGeoData) {
            /*
                NOTE: These reducers/generators can be used to guide HOW to form the data
                      when we roll this out to Client Facing.
            */

            const childData = generateReach(this.props.layerTwo.entity.summary.data);

            reachByGeo = generateReachByGeo(childData);

            for (const metric of metrics) {
                set(childTotals, metric, sumBy(childData, metric));
            }

            for (const child of childData) {
                geoData.push([countryKeys[child.entity_name], child.ecpa_activities]);
                set(reachByGeo, [child.entity_name, 'total'], child.reach);
                for (const metric of metrics) {
                    set(geoDonutData, [metric, child.entity_name],
                        (get(child, metric) / get(childTotals, metric)) * 100
                    );
                }
            }

            orderedGeos = Object.keys(geoDonutData.impressions);

            childSummaryTableData = childData;

            sortedChildSummaryTableData = orderBy(childSummaryTableData, ['impressions', 'ecpa'], ['desc']);

            geoReachTimeSeriesData = generateTimeSeriesReach(childData, this.props.backfillBuckets);

            rvData = rvDataGenerator([
                ...childData,
                {
                    entity_name: MAGIC_TOP_LEVEL_GEO,
                    ecpa: helpers.safeDivide(childTotals.spend, childTotals.ecpa_activities)
                }
            ]);

            geoProductData = generateGeoProductData(childData);

            childTotals.reach = sumBy(geoProductData, 'reach');

            set(reachByGeo, [MAGIC_TOP_LEVEL_GEO, 'total'], childTotals.reach);

            productDonutData = generateProductDonutData(geoProductData);

            orderedProducts = Object.keys(productDonutData.Japan);

            GeoOrProductDonut = topLevelSelected ?
                <GeoDonutChart
                    hideCSVExport={true}
                    exportName="l2-left-donut"
                    title="REACH BY GEO"
                    total={get(childTotals, 'reach', 0)}
                    data={geoDonutData.reach}
                    orderedGeos={orderedGeos}
                    y="reach"
                    totalTitle="Total Reach"
                    totalFormat='0,0'
                    formatName={true}
                    animateDuration={2000} />

                : <GeoDonutChart
                    hideCSVExport={true}
                    exportName="l2-left-donut"
                    title={`PRODUCT REACH - ${country}`}
                    total={get(reachByGeo, [country, 'total'], 0)}
                    data={productDonutData[country]}
                    orderedGeos={orderedProducts}
                    y="reach"
                    totalTitle="Total Reach"
                    totalFormat='0,0'
                    formatName={true}
                    animateDuration={2000} />;


            const filteredProductData = geoProductData.filter(e => e.entity_name === country);

            geoOrProducttwoAxisChart = topLevelSelected ?
                <TwoAxisBarWithScatterChart
                    hideCSVExport={true}
                    title="IMPRESSIONS VS. COST PER SALE BY GEO"
                    exportName="l2-labs-geo-chart-performance"
                    data={sortedChildSummaryTableData}
                    yBar="impressions"
                    scatter="ecpa"
                    scatterTitle={ecpaTerm}
                    toolTipOptions={{
                        pointFormatter: toolTipPointFormatterGenerator
                    }}
                    yScatterOptions={{
                        labels: {
                            formatter: labelFormatterGenerator('priceWithoutSpace')
                        }
                    }}
                    yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                    animateDuration={2000}
                    categories={sortedChildSummaryTableData.map(c => c.entity_name)} />
                :
                <TwoAxisBarWithScatterChart
                    title={`IMPRESSIONS VS. COST PER SALE BY PRODUCT - ${country}`}
                    exportName="l2-labs-geo-chart-performance"
                    data={filteredProductData}
                    yBar="impressions"
                    scatter="ecpa"
                    scatterTitle={ecpaTerm}
                    toolTipOptions={{
                        pointFormatter: toolTipPointFormatterGenerator
                    }}
                    yScatterOptions={{
                        labels: {
                            formatter: labelFormatterGenerator('priceWithoutSpace')
                        }
                    }}
                    yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                    animateDuration={2000}
                    categories={filteredProductData.map(e => e.product)} />;
        }

        return hasData ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    {country}
                </ChannelTitle>
                <Row>
                    <Col md={5}>
                        {hasGeoData ? (
                            <GeoBreakdownMap
                                title='Breakdown by Geo'
                                data={geoData}
                                hasGeoData={hasGeoData}
                                changeCountry={this.changeCountry}
                            />
                        ) : (
                            <RenderingTile height='680px' show={hasGeoData} />
                        )}

                    </Col>
                    <Col md={7}>
                        {hasGeoData ? (
                            <RealValueAudiences
                                title={`Top RealValue Audiences - ${country}`}
                                data={rvData[country]}
                                key={country}
                            />
                        ) : (
                            <RenderingTile height='265px' show={hasGeoData !== {}} />
                        )}

                        {hasGeoData ? (
                            <LineChart
                                hideCSVExport={true}
                                title={`Reach Over Time - ${country}`}
                                exportName={`${stub}-reach-time`}
                                data={
                                    topLevelSelected ?
                                        geoReachTimeSeriesData :
                                        { [country]: geoReachTimeSeriesData[country] }
                                }
                                backfillBuckets={backfillBuckets}
                                y="reach"
                                animateDuration={2000}
                                xAxisType="datetime" />
                        ) : (
                            <RenderingTile height='400px' show={hasGeoData !== {}} />
                        )}
                    </Col>
                </Row>

                <Row>
                    {calloutComponents}
                </Row>

                {/* Child Tables */}
                {hasGeoData ? (
                    <LabsCountrybyProductPivotTable
                        title={'COUNTRY OVERVIEW'}
                        data={geoProductData}
                        country={country}
                        aliasDescriptor="COUNTRY"
                        exportName={`${stub}-table-performance`}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasGeoData} />
                )}

                {/* Donut Charts */}
                <Row>
                    <Col md={6}>
                        {hasGeoData ? (
                            GeoOrProductDonut
                        ) : (
                            <RenderingTile height='400px' show={hasGeoData} />
                        )}
                    </Col>
                    <Col md={6}>
                        {hasGeoData ? (
                            <GeoDonutChart
                                hideCSVExport={true}
                                exportName="l2-right-donut"
                                title={`REACH BY DEVICE - ${country}`}
                                total={get(reachByGeo, [country, 'total'], 0)}
                                data={reachByGeo[country]}
                                orderedGeos={['PC', 'Mobile', 'Other']}
                                y="reach"
                                totalTitle="Total Reach"
                                totalFormat='0,0'
                                formatName={false}
                                animateDuration={2000} />
                        ) : (
                            <RenderingTile height='400px' show={hasGeoData} />
                        )}
                    </Col>
                </Row>

                {hasGeoData ? (
                    geoOrProducttwoAxisChart
                ) : (
                    <RenderingTile height='400px' show={hasGeoData} />
                )}

            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle>Labs - Geo Analysis</ChannelTitle>
                <NoChannelDataTile channel='Summary' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    summary: state.campaign,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    layerTwo: state.layerTwo,
});

export default connect(mapStateToProps, {
    fetchChannelCallouts,
    fetchEntityMeta,
    fetchCampaign,
    fetchCampaignCallouts,
    fetchLayerTwoSummary
})(LabGeoDashboard);



// WEBPACK FOOTER //
// ./src/containers/labs/LabGeoContainer.js